import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  Box,
  Alert
} from '@mui/material';
import {
  Warning as WarningIcon,
  Timer as TimerIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface SessionTimeoutWarningProps {
  warningThreshold?: number; // Minutes before expiry to show warning
  sessionTimeout?: number; // Total session timeout in minutes
}

export const SessionTimeoutWarning: React.FC<SessionTimeoutWarningProps> = ({
  warningThreshold = 5, // 5 minutes before expiry
  sessionTimeout = 30 // 30 minutes total
}) => {
  const { user, logout, refreshToken } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isExtending, setIsExtending] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Update last activity time
  const updateActivity = useCallback(() => {
    setLastActivity(Date.now());
    setShowWarning(false);
  }, []);

  // Check session timeout
  useEffect(() => {
    if (!user) return;

    const checkTimeout = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivity;
      const timeoutMs = sessionTimeout * 60 * 1000;
      const warningMs = warningThreshold * 60 * 1000;
      
      const remaining = timeoutMs - timeSinceActivity;
      
      if (remaining <= 0) {
        // Session expired
        logout();
        return;
      }
      
      if (remaining <= warningMs && !showWarning) {
        setShowWarning(true);
      }
      
      setTimeRemaining(Math.max(0, Math.floor(remaining / 1000)));
    };

    const interval = setInterval(checkTimeout, 1000);
    return () => clearInterval(interval);
  }, [user, lastActivity, sessionTimeout, warningThreshold, showWarning, logout]);

  // Listen for user activity to reset timer
  useEffect(() => {
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      updateActivity();
    };

    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [updateActivity]);

  const handleExtendSession = async () => {
    try {
      setIsExtending(true);
      await refreshToken();
      updateActivity();
      setShowWarning(false);
    } catch (error) {
      console.error('Failed to extend session:', error);
      // If refresh fails, user will be logged out
    } finally {
      setIsExtending(false);
    }
  };

  const handleLogoutNow = () => {
    logout();
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getProgressValue = (): number => {
    const warningMs = warningThreshold * 60 * 1000;
    const progress = (timeRemaining * 1000) / warningMs;
    return Math.max(0, Math.min(100, progress * 100));
  };

  const getProgressColor = (): 'primary' | 'warning' | 'error' => {
    const progress = getProgressValue();
    if (progress > 50) return 'primary';
    if (progress > 20) return 'warning';
    return 'error';
  };

  if (!user || !showWarning) {
    return null;
  }

  return (
    <Dialog
      open={showWarning}
      disableEscapeKeyDown
      onClose={() => {}} // Prevent closing by backdrop click
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="warning" />
          <Typography variant="h6">Session Expiring Soon</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your session will expire due to inactivity
        </Alert>
        
        <Box mb={2}>
          <Typography variant="body1" gutterBottom>
            For your security, you will be automatically logged out in:
          </Typography>
          
          <Box display="flex" alignItems="center" gap={1} my={2}>
            <TimerIcon color="error" />
            <Typography variant="h4" color="error.main">
              {formatTime(timeRemaining)}
            </Typography>
          </Box>
          
          <LinearProgress
            variant="determinate"
            value={getProgressValue()}
            color={getProgressColor()}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
        
        <Typography variant="body2" color="text.secondary">
          Click "Extend Session" to continue working, or "Logout Now" to end your session safely.
        </Typography>
      </DialogContent>
      
      <DialogActions>
        <Button
          onClick={handleLogoutNow}
          color="inherit"
        >
          Logout Now
        </Button>
        <Button
          onClick={handleExtendSession}
          variant="contained"
          color="primary"
          disabled={isExtending}
        >
          {isExtending ? 'Extending...' : 'Extend Session'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeoutWarning;